/* eslint-disable react/no-unused-prop-types */
import { FC, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  Crop,
  makeAspectCrop,
  PixelCrop
} from 'react-image-crop';

import { Button, Modal } from '@components/index';

import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropModalProps {
  isOpen: boolean;
  fileName: string;
  previewUrl: string;
  expectedAspectRatio: number;
  onClose: () => void;
  onConfirm: (croppedFile: File) => void;
}

const ImageCropModal: FC<ImageCropModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  previewUrl,
  fileName,
  expectedAspectRatio
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 50,
    height: 28.125
  });

  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const handleImageLoad = (img: HTMLImageElement) => {
    imageRef.current = img;

    const percentCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 50
        },
        expectedAspectRatio,
        img.naturalWidth,
        img.naturalHeight
      ),
      img.naturalWidth,
      img.naturalHeight
    );
    const pixelCrop = convertToPixelCrop(percentCrop, img.width, img.height);

    setCrop(percentCrop);
    setCompletedCrop(pixelCrop as PixelCrop);
  };

  const handleCompleteCrop = (currentCrop: PixelCrop) => {
    setCompletedCrop(currentCrop);
  };

  const createCroppedImage = async (
    cropRegion: PixelCrop
  ): Promise<File | null> => {
    if (!cropRegion || !imageRef.current || !canvasRef.current) {
      return null;
    }

    const canvas = canvasRef.current;
    const image = imageRef.current;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const pixelRatio = window.devicePixelRatio;

    canvas.width = cropRegion.width * pixelRatio * scaleX;
    canvas.height = cropRegion.height * pixelRatio * scaleY;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      cropRegion.x * scaleX,
      cropRegion.y * scaleY,
      cropRegion.width * scaleX,
      cropRegion.height * scaleY,
      0,
      0,
      canvas.width / pixelRatio,
      canvas.height / pixelRatio
    );

    return new Promise<File | null>((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return resolve(null);
        return resolve(new File([blob], fileName, { type: blob.type }));
      }, 'image/png');
    });
  };

  const handleConfirm = async () => {
    if (completedCrop) {
      const croppedFile = await createCroppedImage(completedCrop);
      if (croppedFile) {
        onConfirm(croppedFile);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose} className="p-6">
      <div className="text-20 font-medium text-content">
        <div className="flex flex-col">
          Crop Branch Logo
          <span className="text-sm font-medium text-gray-500">
            Drag to move and resize the crop area
          </span>
        </div>
      </div>

      {previewUrl && (
        <div className="flex max-h-[526.5px] min-h-[225px] min-w-[400px] max-w-936 items-center justify-center">
          <ReactCrop
            crop={crop}
            aspect={expectedAspectRatio}
            onComplete={handleCompleteCrop}
            onChange={(newCrop) => setCrop(newCrop)}
          >
            <img
              src={previewUrl}
              className="!max-h-[526.5px] !max-w-936"
              alt="Preview"
              onLoad={(e) => handleImageLoad(e.currentTarget)}
            />
          </ReactCrop>
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{
          display: 'none'
        }}
      />

      <div className="mt-4 flex justify-end gap-2">
        <Button label="Cancel" variant="secondary" handleClick={onClose} />
        <Button label="Save" variant="primary" handleClick={handleConfirm} />
      </div>
    </Modal>
  );
};

export default ImageCropModal;
